import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plug, Radio, Clock, AlertTriangle, PowerOff, HelpCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Kleiner Health-Indikator für die Mindbody-Spiegelung.
 * Zeigt einen Status-Badge auf dem Admin-Dashboard, damit der User
 * jederzeit sieht, ob der Read-Only-Mirror live ist.
 *
 * Auto-Refresh alle 30 Sekunden.
 */
const STATUS_CONFIG = {
  live: {
    label: 'Live',
    color: 'bg-green-100 text-green-800 border-green-300',
    Icon: Radio,
    description: 'Real-Time Webhook empfangen',
  },
  ok: {
    label: 'OK',
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    Icon: Plug,
    description: 'Polling-Sync läuft normal',
  },
  stale: {
    label: 'Verzögert',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    Icon: Clock,
    description: 'Letzter Sync >30 Min her',
  },
  error: {
    label: 'Fehler',
    color: 'bg-red-100 text-red-800 border-red-300',
    Icon: AlertTriangle,
    description: 'Letzter Sync-Versuch fehlgeschlagen',
  },
  inactive: {
    label: 'Deaktiviert',
    color: 'bg-gray-100 text-gray-700 border-gray-300',
    Icon: PowerOff,
    description: 'Sync ist ausgeschaltet',
  },
  pending: {
    label: 'Wartet',
    color: 'bg-slate-100 text-slate-700 border-slate-300',
    Icon: Clock,
    description: 'Noch kein Sync gelaufen',
  },
  not_configured: {
    label: 'Nicht eingerichtet',
    color: 'bg-slate-50 text-slate-500 border-slate-200',
    Icon: HelpCircle,
    description: 'Keine Credentials hinterlegt',
  },
};

function formatAge(seconds) {
  if (seconds == null) return '—';
  const s = Math.floor(seconds);
  if (s < 60) return `vor ${s}s`;
  if (s < 3600) return `vor ${Math.floor(s / 60)} Min`;
  if (s < 86400) return `vor ${Math.floor(s / 3600)} h`;
  return `vor ${Math.floor(s / 86400)} T`;
}

const MindbodyHealthBadge = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/mindbody/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHealth(res.data);
    } catch (e) {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="border-gray-200" data-testid="mindbody-health-badge-loading">
        <CardContent className="py-3 px-4 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Mindbody-Status lädt...
        </CardContent>
      </Card>
    );
  }

  if (!health || health.status === 'not_configured') {
    // Bewusst kein Widget, wenn nicht eingerichtet — bleibt dezent
    return null;
  }

  const config = STATUS_CONFIG[health.status] || STATUS_CONFIG.pending;
  const { Icon, label, color, description } = config;
  const ageSource =
    health.status === 'live'
      ? health.last_webhook_age_seconds
      : health.last_sync_age_seconds;
  const ageLabel = formatAge(ageSource);

  return (
    <Card
      className="border hover:shadow-md transition cursor-pointer"
      onClick={() => navigate('/admin/integrations/mindbody')}
      data-testid="mindbody-health-badge"
    >
      <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2 rounded-md border ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900">Mindbody Sync</span>
              <Badge
                variant="outline"
                className={`${color} text-xs font-medium`}
                data-testid="mindbody-health-status"
              >
                {label}
              </Badge>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {description} · {ageLabel}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end text-xs text-gray-500">
          <span data-testid="mindbody-health-slot-count">
            {health.mirror_slot_count ?? 0} Slots
          </span>
          {health.subscription_count > 0 && (
            <span data-testid="mindbody-health-subs">
              {health.subscription_count} Webhook{health.subscription_count > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MindbodyHealthBadge;
